// utils/qwen-asr-leak-guard.ts — ASR biasing-corpus leak guard.
//
// THE BUG (founder live-mic call): the Gummy ASR biasing corpus
// (QWEN_REALTIME_ASR_CONTEXT_LINES_IT, qwen-realtime.ts FP7 §2) leaked as a USER
// transcript. After 2-3 turns the recognizer emitted the ENTIRE concatenated
// corpus ("Autoscuola AI Il Prof Patente B Codice della Strada. spiegami ripeti
// non ho capito ...") as if the student had said it — it became a user turn, was
// answered, and was persisted into the chat thread. This is a known hotword-prompt
// failure mode (the recognizer echoes its bias prompt on noise/silence).
//
// The existing shouldIgnoreQwenUserTranscript filters target short / dangling /
// English-ghost fragments and let this giant in-domain text straight through.
//
// This guard is the deterministic fix: drop any transcript with high overlap to
// the ASR corpus vocabulary. PURE + exported + unit-tested with the founder's
// exact leaked string as a fixture. Applied in the transcript acceptance path of
// useQwenRealtimeVoice (the layer that also protects persistence).
//
// The corpus is mirrored here as a frozen constant. It is STATIC biasing text
// (no ids, no secrets) and must stay in lockstep with the BE source of truth
// ai-tutor/backend/src/gateway/qwen-realtime.ts (QWEN_REALTIME_ASR_CONTEXT_LINES_IT).
// If the BE corpus changes, update this mirror (covered by a shape test there).

// Mirror of backend QWEN_REALTIME_ASR_CONTEXT_LINES_IT (FP7 §2). Keep in sync.
export const QWEN_ASR_CONTEXT_LINES_IT: readonly string[] = [
  'Autoscuola AI Il Prof Patente B Codice della Strada',
  'spiegami ripeti non ho capito cosa significa vero falso esempio',
  'definizioni stradali carreggiata corsia banchina marciapiede spartitraffico',
  'segnali di pericolo cartello triangolare bordo rosso',
  'segnali di divieto segnali di obbligo cartello tondo bordo rosso bordo blu',
  'segnali di precedenza dare precedenza stop precedenza a destra',
  'segnali di indicazione segnali di direzione pannelli integrativi complementari',
  'segnaletica orizzontale strisce di mezzeria attraversamento pedonale linea continua',
  'semaforo agente del traffico segnali luminosi',
  'limiti di velocita centotrenta 130 centodieci 110 novanta 90 cinquanta 50',
  'distanza di sicurezza tempo di reazione spazio di frenata aderenza',
  'norme sulla circolazione posizione sulla carreggiata cambio di corsia',
  'sorpasso divieto di sorpasso manovra di sorpasso',
  'incrocio rotatoria precedenza svolta a destra svolta a sinistra',
  'fermata sosta arresto divieto di sosta zona di parcheggio',
  'segnalazioni luminose dispositivi indicatori di direzione frecce quattro frecce',
  'patenti documenti di circolazione carta di circolazione targa',
  'cinture di sicurezza casco sistemi di ritenuta airbag',
  'comportamento incidente soccorso assicurazione responsabilita',
  'guida in stato di ebbrezza alcol droghe tasso alcolemico zero virgola cinque',
  'cronotachigrafo carrello appendice rimorchio traino massa complessiva',
  'pneumatici catene equipaggiamento manutenzione del veicolo',
  'inquinamento risparmio di carburante guida ecologica ambiente',
  'condizioni del conducente stanchezza sonnolenza uso del cellulare',
  'pioggia nebbia neve ghiaccio visibilita ridotta fari anabbaglianti retronebbia',
  'neopatentati limiti ridotti cento 100 novanta 90 primi tre anni',
  'precedenza sorpasso frenata visibilita comandi dolci sterzo freno',
];

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Build the corpus token bag + the set of normalized corpus LINES once.
const CORPUS_LINES_NORMALIZED: readonly string[] = QWEN_ASR_CONTEXT_LINES_IT.map(normalize).filter(Boolean);
const CORPUS_TOKENS: ReadonlySet<string> = new Set(
  CORPUS_LINES_NORMALIZED.flatMap((line) => line.split(' ')).filter(Boolean),
);

export interface AsrLeakOptions {
  /** Min whole normalized corpus LINES contained verbatim to flag a leak. Default 2. */
  minContainedLines?: number;
  /** Min fraction of a transcript's tokens that are corpus tokens to flag. Default 0.6. */
  minTokenOverlap?: number;
  /** Length (chars) above which a high-corpus-density transcript is flagged. Default 400. */
  longLeakChars?: number;
}

/**
 * Is this user transcript the ASR biasing corpus leaking back (not a real student
 * utterance)? PURE. Flags when ANY of:
 *  (1) it contains >= minContainedLines whole normalized corpus lines verbatim
 *      (the recognizer echoed multiple bias lines — the founder's leak shape), OR
 *  (2) it is long (> longLeakChars) AND > minTokenOverlap of its tokens are corpus
 *      tokens (the giant concatenated dump), OR
 *  (3) it is moderately long (>= 12 tokens) AND > minTokenOverlap corpus-token
 *      overlap AND has near-zero non-corpus content (a dense bias echo).
 *
 * Real student turns — even very in-domain ones ("quanto si va in autostrada con
 * la pioggia?") — are short and mix corpus terms with ordinary words, so they stay
 * well under these thresholds.
 */
export function isAsrContextLeakTranscript(text: string, options: AsrLeakOptions = {}): boolean {
  const {
    minContainedLines = 2,
    minTokenOverlap = 0.6,
    longLeakChars = 400,
  } = options;

  const normalized = normalize(text);
  if (!normalized) return false;
  const tokens = normalized.split(' ').filter(Boolean);
  if (tokens.length < 6) return false; // real short turns can't be a multi-line dump

  // (1) Whole corpus lines echoed verbatim.
  let containedLines = 0;
  for (const line of CORPUS_LINES_NORMALIZED) {
    // Word-boundary-safe containment of a multi-word corpus line.
    if (line.split(' ').length >= 3 && normalized.includes(line)) {
      containedLines += 1;
      if (containedLines >= minContainedLines) return true;
    }
  }

  // Token-overlap density.
  const corpusHits = tokens.filter((t) => CORPUS_TOKENS.has(t)).length;
  const overlap = corpusHits / tokens.length;

  // (2) The giant concatenated dump.
  if (text.length > longLeakChars && overlap > minTokenOverlap) return true;

  // (3) A dense bias echo: long-ish, overwhelmingly corpus vocabulary.
  if (tokens.length >= 12 && overlap > minTokenOverlap) return true;

  return false;
}
